import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Coins, Gift, ShoppingCart, ArrowLeft, Sparkles, CheckCircle2, Wallet, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const fmt = (n) => `${parseFloat(n || 0).toFixed(2).replace(".", ",")}`;

export default function MerchantPackages() {
  const [packages, setPackages] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [busy, setBusy] = useState(false);
  const [kindFilter, setKindFilter] = useState("all");
  const [confirmPkg, setConfirmPkg] = useState(null); // {pkg, method}
  const [paymentMethod, setPaymentMethod] = useState("mp"); // "mp" | "bemcash"

  const refresh = () => api.get("/companies/owner/mine").then((r) => setCompanies(r.data || []));

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data || []));
    refresh().then(() => {
      const stored = localStorage.getItem("mb_selected_company");
      if (stored) setCompanyId(stored);
    });
  }, []);

  useEffect(() => {
    if (!companyId && companies?.[0]?.id) setCompanyId(companies[0].id);
  }, [companies, companyId]);

  const company = companies.find((c) => c.id === companyId);
  const bemcashBalance = parseFloat(company?.company_wallet_balance || 0);

  const openConfirm = (pkg) => {
    if (!companyId) { toast.error("Selecione uma empresa primeiro"); return; }
    setPaymentMethod("mp");
    setConfirmPkg(pkg);
  };

  const proceed = async () => {
    const pkg = confirmPkg;
    setBusy(true);
    try {
      if (paymentMethod === "bemcash") {
        const r = await api.post(`/packages/${pkg.id}/buy-with-bemcash/${companyId}`);
        toast.success(`Pacote creditado! +B$ ${fmt(r.data.voucher_credited)} no saldo de voucher.`);
        setConfirmPkg(null);
        refresh();
      } else {
        const r = await api.post(`/packages/${pkg.id}/buy/${companyId}`);
        const url = r.data.init_point || r.data.sandbox_init_point;
        if (url) {
          // Open in new tab as fallback (some PWAs block window.location)
          const win = window.open(url, "_blank");
          if (!win) {
            window.location.href = url;
          } else {
            toast.success("Abrindo Mercado Pago em nova aba...");
            setConfirmPkg(null);
          }
        } else {
          toast.error("Falha ao gerar link de pagamento");
        }
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha na compra");
    } finally { setBusy(false); }
  };

  const filtered = kindFilter === "all" ? packages : packages.filter((p) => p.kind === kindFilter);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="merchant-packages">
      <Link to="/merchant" className="text-sm text-stone-500 hover:text-stone-700 inline-flex items-center gap-1"><ArrowLeft size={14}/> Voltar</Link>

      <div className="mt-2 mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-blue-950 flex items-center gap-2">
          <ShoppingCart size={28}/> Lojinha de Pacotes
        </h1>
        <p className="text-stone-500 text-sm mt-1">Compre créditos de Cashback ou Voucher pra distribuir aos seus clientes. Pacotes de Voucher podem ser pagos com seu saldo BemCash.</p>
      </div>

      <Card className="p-4 mb-5 rounded-2xl border-stone-200 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs uppercase tracking-wider text-stone-500 font-bold">Empresa que receberá os créditos</label>
          <Select value={companyId} onValueChange={(v) => { setCompanyId(v); localStorage.setItem("mb_selected_company", v); }}>
            <SelectTrigger className="rounded-xl mt-1" data-testid="pkg-company-select"><SelectValue placeholder="Selecione"/></SelectTrigger>
            <SelectContent>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {company && (
          <div className="px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-xs">
            <div className="uppercase tracking-wider text-emerald-700 font-bold">Saldo BemCash</div>
            <div className="font-display text-lg font-bold text-emerald-900" data-testid="pkg-bemcash-balance">B$ {fmt(bemcashBalance)}</div>
          </div>
        )}
        <div>
          <label className="text-xs uppercase tracking-wider text-stone-500 font-bold">Tipo</label>
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger className="rounded-xl mt-1 w-44" data-testid="pkg-kind-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="cashback">Cashback</SelectItem>
              <SelectItem value="voucher">Voucher</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {filtered.map((p) => {
          const isCashback = p.kind === "cashback";
          const accent = isCashback ? "border-emerald-300 from-emerald-50" : "border-blue-300 from-blue-50";
          const Icon = isCashback ? Coins : Gift;
          return (
            <Card key={p.id} className={`p-5 rounded-3xl border-2 ${accent} bg-gradient-to-br to-white relative ${p.highlight ? "shadow-xl shadow-amber-500/10 ring-2 ring-amber-400" : ""}`} data-testid={`pkg-card-${p.id}`}>
              {p.highlight && <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-400 text-amber-950 inline-flex items-center gap-1"><Sparkles size={11}/>Mais popular</span>}
              <div className={`inline-flex items-center gap-2 ${isCashback ? "text-emerald-700" : "text-blue-700"}`}>
                <Icon size={20}/>
                <span className="text-xs uppercase tracking-wider font-bold">{p.kind}</span>
              </div>
              <h3 className="font-display text-xl font-bold text-stone-900 mt-2">{p.name}</h3>
              <p className="text-xs text-stone-600 mt-1 line-clamp-2">{p.description}</p>
              <div className="mt-4 p-3 rounded-2xl bg-white border border-stone-200">
                <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Você recebe</div>
                <div className="font-display text-3xl font-bold text-stone-900">B$ {fmt(p.credits + p.bonus)}</div>
                <div className="text-[11px] text-stone-500 mt-0.5">
                  B$ {fmt(p.credits)} créditos
                  {p.bonus > 0 && <span className="text-emerald-700 font-bold"> + B$ {fmt(p.bonus)} bônus</span>}
                </div>
              </div>
              <div className="mt-3 flex items-baseline justify-between">
                <span className="text-xs text-stone-500">Você paga</span>
                <span className="font-display text-2xl font-bold text-blue-950">R$ {fmt(p.price)}</span>
              </div>
              <Button onClick={() => openConfirm(p)} disabled={!companyId || busy} className={`w-full mt-3 rounded-xl text-white ${isCashback ? "bg-emerald-700 hover:bg-emerald-800" : "bg-blue-900 hover:bg-blue-950"}`} data-testid={`pkg-buy-${p.id}`}>
                <CheckCircle2 size={14} className="mr-1"/>Comprar
              </Button>
            </Card>
          );
        })}
      </div>

      {/* CONFIRM DIALOG */}
      {confirmPkg && (
        <Dialog open onOpenChange={(v) => !v && setConfirmPkg(null)}>
          <DialogContent className="rounded-2xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-2">
                {confirmPkg.kind === "cashback" ? <Coins className="text-emerald-700"/> : <Gift className="text-blue-700"/>}
                Comprar {confirmPkg.name}
              </DialogTitle>
              <DialogDescription className="text-sm">Confirme a compra abaixo. {confirmPkg.kind === "voucher" ? "Pacotes de Voucher podem ser pagos com seu saldo BemCash." : "Pagamento via Mercado Pago."}</DialogDescription>
            </DialogHeader>

            <div className="p-3 rounded-xl bg-stone-50 border border-stone-200 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-stone-500">Você recebe</span><strong className="text-stone-900">B$ {fmt(confirmPkg.credits + confirmPkg.bonus)}</strong></div>
              <div className="flex justify-between"><span className="text-stone-500">Você paga</span><strong className="text-stone-900">R$ {fmt(confirmPkg.price)}</strong></div>
              <div className="flex justify-between"><span className="text-stone-500">Empresa</span><strong className="text-stone-900">{company?.name}</strong></div>
            </div>

            {confirmPkg.kind === "voucher" && (
              <div className="space-y-2 mt-3">
                <div className="text-xs uppercase tracking-wider text-stone-500 font-bold">Forma de pagamento</div>
                <label className={`flex items-start gap-2 p-3 rounded-xl border-2 cursor-pointer ${paymentMethod === "mp" ? "border-blue-400 bg-blue-50" : "border-stone-200"}`}>
                  <input type="radio" name="pm" checked={paymentMethod === "mp"} onChange={() => setPaymentMethod("mp")} className="mt-1" data-testid="pkg-pay-mp"/>
                  <div>
                    <div className="text-sm font-bold flex items-center gap-1"><ExternalLink size={12}/>Mercado Pago</div>
                    <div className="text-xs text-stone-600">Cartão, PIX ou boleto. Abre em nova aba.</div>
                  </div>
                </label>
                <label className={`flex items-start gap-2 p-3 rounded-xl border-2 cursor-pointer ${paymentMethod === "bemcash" ? "border-emerald-400 bg-emerald-50" : "border-stone-200"} ${bemcashBalance < confirmPkg.price ? "opacity-50" : ""}`}>
                  <input type="radio" name="pm" checked={paymentMethod === "bemcash"} onChange={() => setPaymentMethod("bemcash")} disabled={bemcashBalance < confirmPkg.price} className="mt-1" data-testid="pkg-pay-bemcash"/>
                  <div>
                    <div className="text-sm font-bold flex items-center gap-1"><Wallet size={12}/>Saldo BemCash</div>
                    <div className="text-xs text-stone-600">
                      Pagar com seu saldo de B$ {fmt(bemcashBalance)}.
                      {bemcashBalance < confirmPkg.price && <span className="text-red-600 font-bold ml-1">Saldo insuficiente</span>}
                    </div>
                  </div>
                </label>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setConfirmPkg(null)} className="rounded-xl">Cancelar</Button>
              <Button onClick={proceed} disabled={busy || (paymentMethod === "bemcash" && bemcashBalance < confirmPkg.price)} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="pkg-confirm-buy">
                {busy ? "Processando..." : (paymentMethod === "bemcash" ? "Pagar com BemCash" : "Ir para Mercado Pago")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
